import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { VehicleTimePrice } from "../../types/vehicleTimePrice";

interface VehicleType {
  id: string;
  description: string;
}

export default function VehicleTimePriceDetail() {
  const { id } = useParams();
  const [timePrice, setTimePrice] = useState<VehicleTimePrice | null>(null);
  const [vehicleType, setVehicleType] = useState<VehicleType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchTimePrice();
  }, [id]);

  const fetchTimePrice = async () => {
    try {
      const response = await axiosInstance.get(`/vehicletimeprices/${id}`);
      setTimePrice(response.data);
      if (response.data.vehicleTypeId) {
        fetchVehicleType(response.data.vehicleTypeId);
      }
    } catch (error) {
      setError("Failed to fetch time price data");
      console.error("Error fetching time price:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleType = async (vehicleTypeId: string) => {
    try {
      const response = await axiosInstance.get(`/vehicletypes/${vehicleTypeId}`);
      setVehicleType(response.data);
    } catch (error) {
      console.error("Error fetching vehicle type:", error);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!timePrice) return <div>No time price found</div>;

  return (
    <div className="space-y-6">
      <PageBreadcrumb pageTitle="Time Price Details"  />

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Link
          to={`/vehicle-time-prices/${id}`}
          className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
        >
          Edit
        </Link>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        <dl className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500">Vehicle Type</dt>
            <dd className="mt-1 text-lg text-gray-900">
              {vehicleType?.description || 'N/A'}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">From Time</dt>
            <dd className="mt-1 text-lg text-gray-900">
              {timePrice.fromTime}
              {timePrice.fromTimeString && (
                <span className="ml-2 text-sm text-gray-500">
                  ({timePrice.fromTimeString})
                </span>
              )}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">To Time</dt>
            <dd className="mt-1 text-lg text-gray-900">
              {timePrice.toTime}
              {timePrice.toTimeString && (
                <span className="ml-2 text-sm text-gray-500">
                  ({timePrice.toTimeString})
                </span>
              )}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">Price Adjustment</dt>
            <dd className="mt-1 text-lg text-gray-900">
              {timePrice.priceAdjustPers}%
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">Status</dt>
            <dd className="mt-1">
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                timePrice.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {timePrice.status ? 'Active' : 'Inactive'}
              </span>
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
} 